import React, { useEffect, useMemo, useState } from "react";
import { Button } from "components/ui/button";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "components/ui/select";
import { ReactComponent as ClockIcon } from "icons/clock.svg";
import { ReactComponent as CloseIcon } from "icons/close.svg";
import { Icon } from "@iconify/react";
import { toast } from "sonner";
import { useEventHandlers } from "contexts/EventHandlers";
import { useStateContext } from "contexts/ContextProvider";
import { createEvent, updateEvent } from "helpers/projectEvents";
import { irrigationStartSlots, queueEventId, slotIsAvailable, slotStart, timeForMadrid } from "./timeSlots";

const QueueDurationInput = ({
  isAddingDuration,
  setIsAddingDuration,
  preferences,
  setPreferences,
  durationOptions,
  selectedDate,
  currentUserEvent,
  onSubmit,
  isUpdating,
}) => {
  const [loading, setLoading] = useState(false);
  const [eventPersisted, setEventPersisted] = useState(false);
  const { events, setEvents, fetchEventsFromDB } = useEventHandlers();
  const { userProfile } = useStateContext();
  const currentProjectId = userProfile?.currentProjectId;
  const initialDuration = currentUserEvent?.durationMinutes ?? currentUserEvent?.duration ?? preferences.duration ?? 30;
  const initialStart = currentUserEvent ? timeForMadrid(currentUserEvent.start) : "06:00";
  const [duration, setDuration] = useState(initialDuration);
  const [startTime, setStartTime] = useState(initialStart);
  const currentEventId = queueEventId(currentUserEvent);

  useEffect(() => {
    if (!isAddingDuration) return;
    setDuration(currentUserEvent?.durationMinutes ?? currentUserEvent?.duration ?? preferences.duration ?? 30);
    setStartTime(currentUserEvent ? timeForMadrid(currentUserEvent.start) : "06:00");
    setEventPersisted(false);
  }, [isAddingDuration, currentUserEvent?.id, currentUserEvent?.event_id]);

  const availableSlots = useMemo(() => irrigationStartSlots.map((time) => ({
    time,
    available: slotIsAvailable({ selectedDate, time, durationMinutes: duration, events, ignoreEventId: isUpdating ? currentEventId : null }),
  })), [selectedDate, duration, events, isUpdating, currentEventId]);
  const selectedSlotAvailable = Number(duration) > 0 && availableSlots.some(({ time, available }) => time === startTime && available);

  const changeDuration = (value) => {
    const next = Number(value);
    setDuration(next);
    setPreferences((previous) => ({ ...previous, duration: next }));
  };

  const refreshAuthoritativeEvents = async () => {
    const updatedEvents = await fetchEventsFromDB("all", { throwOnError: true });
    setEvents(updatedEvents);
  };

  const handleSubmit = async () => {
    if (loading || !currentProjectId || !selectedSlotAvailable) return;
    setLoading(true);
    let persistedThisAttempt = false;
    try {
      if (eventPersisted) {
        await refreshAuthoritativeEvents();
        onSubmit();
        toast.success("Cola sincronizada correctamente");
        return;
      }
      const start = slotStart(selectedDate, startTime);
      const payload = isUpdating
        ? { type: "queue", start, durationMinutes: Number(duration) }
        : {
            start,
            durationMinutes: Number(duration),
            type: "queue",
            title: "Turno de Riego",
            assigned: `${userProfile?.contactDetails?.firstName || ""} ${userProfile?.contactDetails?.lastName || ""}`.trim(),
            comments: "",
            queryType: "queue",
          };
      const response = isUpdating
        ? await updateEvent(currentProjectId, currentEventId, payload)
        : await createEvent(currentProjectId, payload);
      persistedThisAttempt = true;
      setEventPersisted(true);
      await refreshAuthoritativeEvents();
      onSubmit();
      toast.success(response?.message || (isUpdating ? "Turno actualizado satisfactoriamente" : "Turno creado satisfactoriamente"));
    } catch (error) {
      if (error?.response?.status === 409 || error?.status === 409) {
        try { await refreshAuthoritativeEvents(); } catch { /* retain the useful conflict message */ }
        toast.error("Ese horario ya no está disponible. Selecciona otro turno.");
      } else {
        toast.error((eventPersisted || persistedThisAttempt) ? "El turno se creó, pero no se pudo sincronizar la cola." : (error.message || "Error guardando el turno de riego"));
      }
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className={`${isAddingDuration ? "scroll-gro w-upwards" : "scroll-grow-upwards"} grow gap-y-1 text-gray-700 gap-x-1 flex bg-gray-900 dark:bg-gray-100 flex-col border-gray-200 rounded-md px-3 py-2.5`}>
      <div className="flex justify-between items-center flex-row">
        <div className="mb-2 flex gap-x-2 justify-center text-center text-white dark:text-gray-950 pt-2"><ClockIcon className="w-6 h-6" />Configura tu turno de riego</div>
        <Button size="icon" className="shrink-0 size-8" variant="secondary" onClick={() => setIsAddingDuration(false)}><CloseIcon className="w-5 h-5" /></Button>
      </div>
      <label className="text-white dark:text-gray-950" htmlFor="queue-start-time">Hora de inicio</label>
      <Select value={startTime} onValueChange={setStartTime}>
        <SelectTrigger id="queue-start-time" className="bg-white dark:hover:bg-gray-200"><SelectValue placeholder="Selecciona una hora">{startTime}</SelectValue></SelectTrigger>
        <SelectContent>{availableSlots.map(({ time, available }) => <SelectItem key={time} value={time} disabled={!available}>{time}{available ? "" : " — ocupado"}</SelectItem>)}</SelectContent>
      </Select>
      <label className="text-white dark:text-gray-950" htmlFor="queue-duration">Duración</label>
      <Select value={duration} onValueChange={changeDuration}>
        <SelectTrigger id="queue-duration" className="bg-white dark:hover:bg-gray-200"><SelectValue placeholder="Selecciona una duración">{durationOptions.find((option) => option.value === duration)?.label || "Selecciona una duración"}</SelectValue></SelectTrigger>
        <SelectContent>{durationOptions.map((option) => <SelectItem key={option.value} value={option.value}>{option.label}</SelectItem>)}</SelectContent>
      </Select>
      {!selectedSlotAvailable && <p className="text-sm text-red-200 dark:text-red-700">La combinación de hora y duración ya no está disponible.</p>}
      <Button size="default" className="w-full mt-2" variant="secondary" onClick={handleSubmit} disabled={loading || !selectedSlotAvailable}>
        {loading ? <Icon icon="svg-spinners:180-ring" className="dark:text-white" /> : eventPersisted ? "Reintentar sincronización" : "Guardar"}
      </Button>
    </div>
  );
};

export default QueueDurationInput;
